import React from "react";
import { Box, Button, Tooltip, alpha, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ClearAll as ClearAllIcon, PlayArrow } from "@mui/icons-material";
import SelectedVideosList from "../SelectedVideosList";
import VideoUploader from "../VideoUploader";
import { VideoPreview } from "../../../shared/interfaces/VideoPreview";

interface SelectionControlsProps {
  selectedVideos: string[];
  allVideos: VideoPreview[];
  onRemoveVideo: (id: string) => void;
  onClearSelection: () => void;
  onProcessVideos: () => void;
  fetchVideos: () => void;
  allowUpload: boolean;
}

export const SelectionControls: React.FC<SelectionControlsProps> = ({
  selectedVideos,
  allVideos,
  onRemoveVideo,
  onClearSelection,
  onProcessVideos,
  fetchVideos,
  allowUpload,
}) => {
  const theme = useTheme();

  return (
    <Grid size={{ xs: 12 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Display list of currently selected videos */}
        <SelectedVideosList
          selectedVideos={selectedVideos}
          videos={allVideos}
          onRemove={onRemoveVideo}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {selectedVideos.length > 0 && (
              <Tooltip title="Clear all selected videos" arrow>
                <Button
                  variant="outlined"
                  onClick={onClearSelection}
                  startIcon={<ClearAllIcon />}
                  sx={{
                    textTransform: "none",
                    height: 42,
                    px: 2.5,
                    fontWeight: 600,
                    borderWidth: 2,
                    borderColor: alpha(theme.palette.grey[500], 0.5),
                    color: "text.secondary",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderWidth: 2,
                      borderColor: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.04),
                      color: theme.palette.error.main,
                      transform: "translateY(-2px)",
                      boxShadow: `0 4px 12px 0 ${alpha(theme.palette.error.main, 0.2)}`,
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:focus-visible": {
                      outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  Clear Selection
                </Button>
              </Tooltip>
            )}
            {/* Video upload component - only enabled for KARDA */}
            <VideoUploader
              fetchVideos={fetchVideos}
              allowUpload={allowUpload}
            />
          </Box>
          <Tooltip
            title={
              selectedVideos.length === 0
                ? "Select videos to continue"
                : "Proceed to review selected videos"
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                disabled={selectedVideos.length === 0}
                onClick={onProcessVideos}
                startIcon={<PlayArrow />}
                sx={{
                  textTransform: "none",
                  height: 48,
                  px: 4,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 2,
                  background:
                    selectedVideos.length === 0
                      ? undefined
                      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow:
                    selectedVideos.length === 0
                      ? undefined
                      : `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.5)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    outlineOffset: 2,
                  },
                  "&.Mui-disabled": {
                    background: undefined,
                    boxShadow: "none",
                  },
                }}
              >
                View Selected Videos
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Grid>
  );
};
